import { useUserStore } from '@qse/stores'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  assignQpaSeeds,
  getTenantQuotaSummary,
  getUserQuotaSummary,
  revokeQpaSeeds,
} from '../quota'
import { queryKeys } from '../react-query-config'
import type {
  AssignQpaSeedsDto,
  QpaAssignmentResponseDto,
  QuotaSummaryDto,
  RevokeQpaSeedsDto,
  TenantQuotaSummaryDto,
} from '../types'

/**
 * Hook to get user quota summary
 */
export const useUserQuotaSummary = (options?: { enabled?: boolean }) => {
  const { user } = useUserStore()

  const isUserLoaded = !!user?.userRole // or more explicit if needed

  return useQuery<QuotaSummaryDto>({
    queryKey: queryKeys.quota.userSummary(),
    queryFn: getUserQuotaSummary,
    staleTime: 0,
    gcTime: 10 * 60 * 1000,
    enabled: isUserLoaded && options?.enabled !== false && user.userRole !== 'SUPER_ADMIN',
  })
}
/**
 * Hook to get tenant quota summary for tenant admin
 */
export const useTenantQuotaSummary = (options?: {
  enabled?: boolean
  tenantStatus?: string
  tenantId?: string
}) => {
  const { user } = useUserStore()

  const isUserLoaded = !!user?.userRole // or more explicit if needed

  // Check if tenant status prevents API calls
  const isTenantStatusBlocked =
    options?.tenantStatus && ['incomplete', 'suspended', 'cancelled'].includes(options.tenantStatus)

  return useQuery<TenantQuotaSummaryDto>({
    queryKey: queryKeys.quota.tenantSummary(options?.tenantId),
    queryFn: () => {
      // Double-check the blocking condition inside the query function
      if (isTenantStatusBlocked) {
        // Return a rejected promise to prevent the API call
        return Promise.reject(new Error('API call blocked due to tenant status'))
      }

      return getTenantQuotaSummary()
    },
    staleTime: 0, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled:
      isUserLoaded &&
      options?.enabled !== false &&
      user.userRole !== 'TENANT_USER' &&
      !isTenantStatusBlocked,
  })
}

/**
 * Hook to assign QPA seeds to a user (TENANT_ADMIN only)
 */
export const useAssignQpaSeeds = (tenantId?: string) => {
  const queryClient = useQueryClient()

  return useMutation<QpaAssignmentResponseDto, Error, AssignQpaSeedsDto>({
    mutationFn: assignQpaSeeds,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.quota.tenantSummary(tenantId) })
    },
  })
}

/**
 * Hook to revoke QPA seeds from a user (TENANT_ADMIN only)
 */
export const useRevokeQpaSeeds = (tenantId?: string) => {
  const queryClient = useQueryClient()

  return useMutation<QpaAssignmentResponseDto, Error, RevokeQpaSeedsDto>({
    mutationFn: revokeQpaSeeds,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.quota.tenantSummary(tenantId) })
    },
  })
}
